import { useState, useEffect, useRef, useCallback } from 'react';
import { useCreatorCache } from '../../../../contexts/CreatorCacheContext';
import { apiClient } from '../../../../config/apiConfig';
import { toast } from 'react-toastify';
import { creatorService } from '../../../../services/api';
import { matchesCreatorStatusFilter } from '../../../../constants/creatorReview';

const revenueTotal = creator => Number.isFinite(creator.revenue?.total) ? creator.revenue.total : 0;

export const sortMapping = {
  'username': (a, b) => (a.twitter?.username || '').localeCompare(b.twitter?.username || ''),
  'followers': (a, b) => (b.twitter?.followers || 0) - (a.twitter?.followers || 0),
  'score': (a, b) => (b.reliabilityScore || 0) - (a.reliabilityScore || 0),
  'lastActivity': (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0),
  'revenue': (a, b) => revenueTotal(b) - revenueTotal(a),
  'impressions': (a, b) => (b.estimatedImpressions || 0) - (a.estimatedImpressions || 0)
};

export function useCreatorManagement() {
  const {
    loading: cacheLoading,
    isInitialized,
    loadProgress,
    error,
    totalCreators,
    hasMoreCreators,
    loadingMore,
    getAllCreators,
    searchCreators,
    updateCreator,
    addCreator,
    removeCreator,
    refreshCache,
    loadMoreCreators,
    fetchAndUpdateCreator
  } = useCreatorCache();

  // Local state
  const [isUploading, setIsUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCreators, setFilteredCreators] = useState([]);
  const [viewMode, setViewMode] = useState('cards');
  const [sortBy, setSortBy] = useState('username');
  const [showAddWizard, setShowAddWizard] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [syncStatus, setSyncStatus] = useState({});
  const [batchSyncStatus, setBatchSyncStatus] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailCreator, setDetailCreator] = useState(null);
  const [selectedCreators, setSelectedCreators] = useState(new Set());
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [deleteConfirmState, setDeleteConfirmState] = useState({ open: false, creatorId: null });
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);

  const fileInputRef = useRef(null);
  const batchSyncIntervalRef = useRef(null);

  // Complete the directory automatically while this page is open, using bounded cache pages.
  // A failed page pauses here until the user retries, rather than issuing an automatic retry loop.
  useEffect(() => {
    if (isInitialized && hasMoreCreators && !cacheLoading && !loadingMore && !error) {
      loadMoreCreators();
    }
  }, [isInitialized, hasMoreCreators, cacheLoading, loadingMore, error, loadMoreCreators]);

  // Handle creator selection for review
  const handleSelectCreator = useCallback((creatorId) => {
    setSelectedCreators(prev => {
      const newSet = new Set(prev);
      if (newSet.has(creatorId)) {
        newSet.delete(creatorId);
      } else {
        newSet.add(creatorId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      const allIds = filteredCreators.map(c => c._id);
      setSelectedCreators(new Set(allIds));
    } else {
      setSelectedCreators(new Set());
    }
  }, [filteredCreators]);

  const handleOpenReviewModal = useCallback(() => {
    if (selectedCreators.size === 0) {
      toast.error('Please select at least one creator to review');
      return;
    }
    setShowReviewModal(true);
  }, [selectedCreators]);

  const handleCloseReviewModal = useCallback(() => {
    setShowReviewModal(false);
    setSelectedCreators(new Set());
  }, []);

  // Handle creator added from wizard
  const handleCreatorAdded = useCallback((newCreatorData) => {
    addCreator(newCreatorData);
    setShowAddWizard(false);
    toast.success(`Creator @${newCreatorData.twitter.username} added successfully!`);
  }, [addCreator]);

  // Sync status polling
  useEffect(() => {
    const pollSyncStatus = async (creatorId) => {
      try {
        const response = await creatorService.syncCreatorFollowers(creatorId);
        if (response.success) {
          setSyncStatus(prev => ({
            ...prev,
            [creatorId]: {
              status: 'completed',
              message: `Synced: ${response.followerCount} followers`,
              timestamp: new Date()
            }
          }));
          fetchAndUpdateCreator(creatorId);
        } else {
          setSyncStatus(prev => ({
            ...prev,
            [creatorId]: {
              status: 'error',
              message: response.error || 'Sync failed',
              timestamp: new Date()
            }
          }));
          toast.error(`Failed to sync followers: ${response.error}`);
        }
      } catch (error) {
        console.error('Error syncing followers:', error);
        setSyncStatus(prev => ({
          ...prev,
          [creatorId]: {
            status: 'error',
            message: error.message || 'Sync failed',
            timestamp: new Date()
          }
        }));
        toast.error(`Failed to sync followers: ${error.message}`);
      }
    };

    const interval = setInterval(() => {
      Object.entries(syncStatus).forEach(([creatorId, status]) => {
        if (status.status === 'syncing') {
          pollSyncStatus(creatorId);
        }
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [syncStatus, fetchAndUpdateCreator]);

  // Function to start sync for a creator
  const handleSyncFollowers = useCallback(async (creatorId) => {
    try {
      setIsSyncing(true);
      const result = await creatorService.syncCreatorFollowers(creatorId);
      toast.success(result.message || 'Followers synced successfully');
      await fetchAndUpdateCreator(creatorId);
    } catch (error) {
      console.error('Error syncing followers:', error);
      toast.error(error.message || 'Failed to sync followers');
    } finally {
      setIsSyncing(false);
    }
  }, [fetchAndUpdateCreator]);

  // Function to sync profile images for all creators
  const handleBulkProfileImageSync = useCallback(async () => {
    try {
      setBatchSyncStatus({ status: 'syncing', message: 'Starting profile image sync...' });

      const response = await apiClient.post('twitter/bulk-sync-profile-images');

      if (response.data.success) {
        const results = response.data.results || { success: response.data.updatedCount || 0, failed: 0, skipped: 0 };
        toast.success(`Profile image sync completed: ${results.success} updated, ${results.failed} failed, ${results.skipped} skipped`);
        console.log('Bulk profile image sync response:', response.data);
        await refreshCache();
      } else {
        throw new Error(response.data.message || 'Sync failed');
      }

      setBatchSyncStatus(null);
    } catch (error) {
      console.error('Error in bulk profile image sync:', error);
      setBatchSyncStatus({
        status: 'error',
        message: error.message || 'Profile image sync failed'
      });
      toast.error(`Failed to sync profile images: ${error.response?.data?.message || error.message}`);

      setTimeout(() => {
        setBatchSyncStatus(null);
      }, 5000);
    }
  }, [refreshCache]);

  // Function to trigger batch follower sync
  const handleBatchFollowerSync = useCallback(async () => {
    try {
      setBatchSyncStatus({
        status: 'syncing',
        message: 'Starting Twitter follower sync...',
        totalCreators,
        completedCreators: 0
      });

      const response = await creatorService.post('twitter/sync/followers');
      toast.success('Twitter follower sync started successfully');
      console.log('Sync response:', response.data);

      // Track progress so a stalled background job can be reported truthfully.
      let lastCompletedCount = 0;
      let stuckCounter = 0;

      // Poll for sync status
      const interval = setInterval(async () => {
        try {
          const statusResponse = await creatorService.get('twitter/sync/followers/status');
          const status = statusResponse.data;

          if (!status || status.status === 'none') {
            clearInterval(interval);
            setBatchSyncStatus(null);
            toast.warning('Follower sync status is unavailable. Refresh the creator list to check updated counts.');
            refreshCache();
            return;
          }

          if (status.status === 'completed') {
            clearInterval(interval);
            setBatchSyncStatus(null);
            const failedCount = status.errorList?.length || 0;
            if (failedCount) {
              toast.warning(`Follower sync finished: ${status.completed || 0}/${status.total || 0} completed, ${failedCount} errors`);
            } else {
              toast.success(`Follower sync completed: ${status.completed || 0}/${status.total || 0} creators`);
            }
            refreshCache();
          } else if (status.status === 'error') {
            clearInterval(interval);
            setBatchSyncStatus(null);
            toast.error('Error syncing Twitter followers: ' + (status.error || 'The sync did not complete'));
            refreshCache();
          } else {
            // Check if progress has stalled
            const completedCount = status.completed || 0;

            if (completedCount === lastCompletedCount) {
              stuckCounter++;

              // After 2 minutes of no progress (6 checks at 20 second intervals)
              if (stuckCounter >= 6) {
                clearInterval(interval);
                setBatchSyncStatus(null);
                toast.warning(`Twitter sync appears to be stuck at ${completedCount}/${status.total} creators. The process will continue in the background.`);
                refreshCache();
                return;
              }
            } else {
              stuckCounter = 0;
              lastCompletedCount = completedCount;
            }

            setBatchSyncStatus({
              status: status.status,
              totalCreators: status.total,
              completedCreators: completedCount,
              message: `Syncing Twitter followers (${completedCount}/${status.total})`
            });
          }
        } catch (error) {
          console.error('Error checking sync status:', error);
          stuckCounter++;

          if (stuckCounter >= 3) {
            clearInterval(interval);
            setBatchSyncStatus(null);
            toast.error('Sync status check failed multiple times. The process may continue in the background.');
            refreshCache();
          }
        }
      }, 20000);
      batchSyncIntervalRef.current = interval;
    } catch (error) {
      console.error('Failed to start follower sync:', error);
      toast.error(error.response?.data?.message || 'Failed to start Twitter follower sync');
      setBatchSyncStatus(null);
    }
  }, [refreshCache, totalCreators]);

  // Apply search and filter from cache whenever they change
  useEffect(() => {
    let results = getAllCreators();

    if (searchTerm) {
      results = searchCreators(searchTerm);
    }

    if (filterStatus && filterStatus !== 'all' && filterStatus !== '__all__') {
      results = results.filter(c => matchesCreatorStatusFilter(c, filterStatus));
    }

    if (filterType && filterType !== 'all' && filterType !== '__all__') {
      results = results.filter(c => c.creatorType?.includes(filterType));
    }

    if (sortBy && sortMapping[sortBy]) {
      results = [...results].sort(sortMapping[sortBy]);
    }

    setFilteredCreators(results);
  }, [searchTerm, filterStatus, filterType, sortBy, getAllCreators, searchCreators]);

  // View details
  const handleViewDetails = useCallback((creator, event) => {
    if (event) {
      event.stopPropagation();
    }
    setDetailCreator(creator);
    setShowDetailModal(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setShowDetailModal(false);
    setDetailCreator(null);
  }, []);

  // Detail modal update handler
  const handleCreatorUpdate = useCallback((updatedCreator) => {
    setDetailCreator(current => current?._id === updatedCreator._id ? {
      ...current,
      ...updatedCreator,
      wallet: {
        ...(current?.wallet || {}),
        ...(updatedCreator?.wallet || {})
      }
    } : current);
    updateCreator(updatedCreator._id, updatedCreator);
  }, [updateCreator]);

  // Delete flow with ConfirmDialog
  const handleDeleteCreator = useCallback((creatorId) => {
    setDeleteConfirmState({ open: true, creatorId });
  }, []);

  const confirmDelete = useCallback(async () => {
    const creatorId = deleteConfirmState.creatorId;
    setDeleteConfirmState({ open: false, creatorId: null });

    console.log('Attempting to delete creator with ID:', creatorId);
    try {
      const response = await apiClient.delete(`/creators/${creatorId}`);

      if (response.status === 200 || response.status === 204) {
        removeCreator(creatorId);
        toast.success('Creator deleted successfully');
      }
    } catch (error) {
      console.log('Delete error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });

      if (error.response?.status === 404) {
        console.log('Creator not found (404) - removing from cache');
        toast.info('Creator not found in database - removing from list');
        removeCreator(creatorId);
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.');
      } else {
        console.error('Error deleting creator:', error);
        toast.error(`Failed to delete creator: ${error.response?.data?.message || error.message}`);
      }
    }
  }, [deleteConfirmState.creatorId, removeCreator]);

  const handleDeleteAll = useCallback(() => {
    setDeleteAllConfirmOpen(true);
  }, []);

  const confirmDeleteAll = useCallback(async () => {
    setDeleteAllConfirmOpen(false);

    try {
      await creatorService.deleteAllCreators();
      toast.success('All creators deleted successfully');
      refreshCache();
    } catch (error) {
      console.error('Error deleting all creators:', error);
      toast.error('Failed to delete all creators');
    }
  }, [refreshCache]);

  // Handle file upload
  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setBatchSyncStatus({ status: 'uploading', message: 'Uploading file...' });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('isInitialMetricsLoad', 'true');

      const response = await creatorService.uploadCreatorFile(formData);
      console.log('Upload response:', response);

      const metricsInfo = response.data.stats?.metricsAdded > 0
        ? ` and added advanced metrics for ${response.data.stats.metricsAdded} creators`
        : '';

      setBatchSyncStatus({
        status: 'completed',
        totalCreators: response.data.stats?.total || response.data.total,
        completedCreators: response.data.stats?.total || response.data.total,
        message: `Successfully imported ${response.data.stats?.total || response.data.total} creators${metricsInfo}. Use the "Sync All Followers" button to update Twitter follower counts.`
      });

      setTimeout(() => {
        setBatchSyncStatus(null);
      }, 10000);

      await refreshCache();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.error || 'Error uploading file');
      setBatchSyncStatus(null);
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  }, [refreshCache]);

  // Export function
  const exportCreators = useCallback(() => {
    const allCreators = getAllCreators();
    const headers = [
      'twitter_username',
      'profile_link',
      'followers',
      'advanced_score',
      'advanced_content_quality',
      'smart_followers'
    ];
    const data = allCreators.map(creator => ({
      twitter_username: creator.twitter?.username || '',
      profile_link: creator.twitter?.username ? `https://x.com/${creator.twitter.username}` : '',
      followers: creator.twitter?.followers || 0,
      advanced_score: creator.advancedMetrics?.score || 0,
      advanced_content_quality: creator.advancedMetrics?.contentQuality || 0,
      smart_followers: creator.advancedMetrics?.smartFollowersCount || 0
    }));

    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => row[header]).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'creators.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [getAllCreators]);

  // Cleanup status polling on unmount.
  useEffect(() => {
    return () => {
      if (batchSyncIntervalRef.current) {
        clearInterval(batchSyncIntervalRef.current);
      }
    };
  }, []);

  return {
    // State
    filteredCreators,
    searchTerm,
    setSearchTerm,
    filterStatus,
    setFilterStatus,
    filterType,
    setFilterType,
    sortBy,
    setSortBy,
    viewMode,
    setViewMode,
    selectedCreators,
    showAddWizard,
    setShowAddWizard,
    showDetailModal,
    detailCreator,
    showReviewModal,
    batchSyncStatus,
    isUploading,
    isSyncing,
    deleteConfirmState,
    deleteAllConfirmOpen,

    // Cache context pass-through
    cacheLoading,
    isInitialized,
    loadProgress,
    error,
    totalCreators,
    hasMoreCreators,
    loadingMore,
    getAllCreators,
    refreshCache,
    loadMoreCreators,
    fetchAndUpdateCreator,

    // Handlers
    handleSelectCreator,
    handleSelectAll,
    handleOpenReviewModal,
    handleCloseReviewModal,
    handleCreatorAdded,
    handleSyncFollowers,
    handleBatchFollowerSync,
    handleBulkProfileImageSync,
    handleViewDetails,
    handleCloseDetail,
    handleCreatorUpdate,
    handleDeleteCreator,
    confirmDelete,
    handleDeleteAll,
    confirmDeleteAll,
    handleFileUpload,
    exportCreators,
    fileInputRef
  };
}
